import { useQuery } from '@apollo/react-hooks';
import type { ApolloError } from 'apollo-client';

import type FeatureFlagClient from '@atlaskit/feature-flag-client';

import { fg } from '@confluence/feature-gating';

import { SpaceDetailQuery } from './SpaceDetailQuery.graphql';

export type SpaceDetailDataType = {
	spaceId: string;
	type: string;
	name: string;
	alias?: string;
	dataClassificationTags: string[];
};

interface CommonHookResult {
	loading: boolean;
	error?: ApolloError;
}

export interface SpaceDetailType extends CommonHookResult {
	data?: SpaceDetailDataType;
}

export const useSpaceDetail = (
	spaceKey: string | null | undefined,
	featureFlagClient?: FeatureFlagClient,
): SpaceDetailType => {
	const isSpaceAliasFFEnabled = !!featureFlagClient?.getBooleanValue(
		'confluence.frontend.space.alias',
		{ default: false, shouldTrackExposureEvent: true },
	);

	const { loading, data, error } = useQuery(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		SpaceDetailQuery,
		{
			variables: {
				includeKey: isSpaceAliasFFEnabled,
				spaceKey,
				includeAlias: isSpaceAliasFFEnabled && fg('confluence_modernized_breadcrumbs'),
			},
		},
	);

	return {
		loading,
		data: data && {
			spaceId: data.space?.id,
			type: data.space?.type,
			name: data.space?.name,
			dataClassificationTags: data.space?.dataClassificationTags,
		},
		error,
	};
};
